// Iter79 — Wheel-style Strategy Result Card.
//
// Reusable visual shell that polishes the per-symbol scanner result
// rendering across BPCS / BCCS / Iron Condor / CFM-Shares /
// CFM-LEAPS into a single Wheel-style card layout:
//
//   ┌───────────────────────────────────────────────────────────┐
//   │  SYMBOL  · module badge       [STATUS]  decision   …      │
//   │  CONF 78   RISK 32   regime   reasons …                   │
//   │  ──────────────────────────────────────────────────────── │
//   │  <module-specific metric children />                      │
//   │  warnings / error                                         │
//   │  plain-English summary                                    │
//   │  ▸ Selected structure / option plan  (collapsed)          │
//   │  ▸ Raw result JSON                   (collapsed)          │
//   └───────────────────────────────────────────────────────────┘
//
// HARD CONSTRAINTS:
//  * Pure-static render. No HTTP. No useEffect. No useState.
//  * No endpoint references. No strategy logic. No ranking.
//  * Status colour pulled from the Iter73 STATUS_COLOR_MAP — never
//    redeclared. Colour is never the only status indicator: the
//    status text code, the decision label, and the bucket-aware
//    chip labels are all rendered as text.
//  * Every prior-iteration per-row testid is preserved verbatim
//    via the `testidPrefix` prop.

import React from "react";
import {
  STATUS_COLOR_MAP, STATUS_NEUTRAL_DEFAULT,
  ReasonCodeChips, ScannerWarnings, ScannerErrorMessage,
} from "@/components/ScannerResultNormalization";

export default function StrategyResultCard({
  rec, moduleLabel, accent, testidPrefix, children,
}) {
  if (!rec || typeof rec !== "object") return null;
  const sym = rec.symbol || "—";
  const a = accent || { border: "border-[#26262B]", glow: "bg-[#0A0A0E]", text: "text-[#E0E0E0]" };

  const statusClass = STATUS_COLOR_MAP[rec.status] || STATUS_NEUTRAL_DEFAULT;
  const hasReasons = Array.isArray(rec.reason_codes) && rec.reason_codes.length > 0;
  const hasWarnings = Array.isArray(rec.warnings) && rec.warnings.length > 0;

  return (
    <li
      data-testid={`${testidPrefix}-${sym}`}
      className={`rounded-sm border ${a.border} ${a.glow} p-3 space-y-2`}
    >
      {/* Header: bold ticker · module label · status · decision · conf · risk · regime */}
      <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono">
        <span
          data-testid={`${testidPrefix}-symbol-${sym}`}
          className={`text-[14px] font-bold uppercase tracking-[0.14em] ${a.text}`}
          aria-label={`Symbol ${sym}`}
        >
          {sym}
        </span>
        <span
          className={`px-1.5 py-0.5 rounded-sm border ${a.border} ${a.text} text-[9px] uppercase tracking-[0.14em]`}
          aria-label={`Module ${moduleLabel}`}
        >
          {moduleLabel}
        </span>
        <span
          data-testid={`${testidPrefix}-status-${sym}`}
          className={`px-2 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.14em] ${statusClass}`}
          aria-label={`Status ${rec.status}`}
        >
          {rec.status}
        </span>
        {rec.decision && rec.decision !== rec.status && (
          <span
            data-testid={`${testidPrefix}-decision-${sym}`}
            className="text-[#8A8A93] uppercase tracking-[0.12em]"
          >
            {rec.decision}
          </span>
        )}
        <span
          data-testid={`${testidPrefix}-conf-${sym}`}
          className="text-[#E0E0E0]"
        >
          CONF {rec.confidence_score ?? 0}
        </span>
        <span
          data-testid={`${testidPrefix}-risk-${sym}`}
          className="text-[#FFB800]"
        >
          RISK {rec.risk_score ?? 0}
        </span>
        {rec.regime && (
          <span
            data-testid={`${testidPrefix}-regime-${sym}`}
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 text-[9px] uppercase tracking-[0.12em] text-[#FFB800]"
          >
            {rec.regime}
          </span>
        )}
        {hasReasons && (
          <ReasonCodeChips
            codes={rec.reason_codes}
            testid={`${testidPrefix}-reasons-${sym}`}
          />
        )}
      </div>

      {/* Module-specific metric children slot */}
      {children}

      {/* Top-level per-row warnings */}
      {hasWarnings && (
        <ScannerWarnings
          warnings={rec.warnings}
          testid={`${testidPrefix}-warnings-${sym}`}
        />
      )}

      {/* Per-row error block */}
      {rec.error_message && (
        <ScannerErrorMessage
          message={rec.error_message}
          testid={`${testidPrefix}-error-${sym}`}
        />
      )}

      {/* Plain-English summary */}
      {(rec.plain_english || rec.summary) && (
        <div
          data-testid={`${testidPrefix}-plain-${sym}`}
          className="text-[11px] font-mono text-[#E0E0E0] leading-relaxed"
        >
          {rec.plain_english || rec.summary}
        </div>
      )}

      {/* Collapsed: selected structure / option plan */}
      {(rec.selected_structure || rec.option_plan) && (
        <details
          data-testid={`${testidPrefix}-structure-${sym}`}
          className="mt-1"
        >
          <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
            Selected structure / option plan
          </summary>
          <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
            {JSON.stringify(rec.selected_structure || rec.option_plan, null, 2)}
          </pre>
        </details>
      )}

      {/* Collapsed: raw result JSON */}
      <details
        data-testid={`${testidPrefix}-raw-${sym}`}
        className="mt-1"
      >
        <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Raw result JSON
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
          {JSON.stringify(rec, null, 2)}
        </pre>
      </details>
    </li>
  );
}
